import React, {useState, useEffect} from 'react';
import {IconButton, Typography} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import './ImageEditor.scss';
import {Helmet} from 'react-helmet';
import {generateCroppedUrl, removeCropParameters} from "../../../Utils/imageTools/imageCropper";
import { addMediaLibraryPost } from 'features/meta-travel-ads-campaign/Utils/getMediaLibrary';
import CustomCheckbox from 'components/Checkbox';

export const APP_CLOUDINARY_CLOUDNAME = process.env.REACT_APP_CLOUDINARY_CLOUDNAME;
export const APP_CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
export const APP_CLOUDINARY_URL_ENDPOINT = process.env.REACT_APP_CLOUDINARY_URL_ENDPOINT;
export const CLOUDINARY_IMAGE_CATEGORIES = ['marketing'];

export const DEFAULT_RATIOS = {
    marketing: 16 / 9,
};

const CloudinaryImageEditor = ({
    landscape,
    setLandscape,
    hotelId,
    errors = {},
    onChangeSelection,
    initialSelectedImage = null,
}) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [fullScreenImage, setFullScreenImage] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedMarketingImage,setSelectedMarketingImage] = useState(initialSelectedImage);

    useEffect(() => {
        setSelectedMarketingImage(initialSelectedImage);
    }, [initialSelectedImage]);

    const openUploadWidget = (category) => {
        const defaultRatio = DEFAULT_RATIOS[category];
        if (!defaultRatio) return;

        window.cloudinary.openUploadWidget(
            {
                cloudName: APP_CLOUDINARY_CLOUDNAME,
                uploadPreset: APP_CLOUDINARY_UPLOAD_PRESET,
                multiple: true, // ✅ Allow multiple file uploads
                sources: ['local', 'url'],
                cropping: false, // ❌ Disable manual cropping UI
                croppingAspectRatio: defaultRatio, // ✅ Still sets default ratio internally
                showSkipCropButton: false,
                transformation: [
                    {
                        aspect_ratio: `${Math.round(defaultRatio * 100)}:${100}`,
                        crop: 'fill',
                    },
                ],
            },
            async (error, result) => {
                if (!error && result?.event === 'success') {
                    const croppedUrl = generateCroppedUrl(result.info);
                    await Promise.all([
                        (async () => {
                            updateImageCategory(category, croppedUrl);
                        })(),
                    ]);
                }
            }
        );
    };

    console.log(hotelId);
    

    const handleAddPostToMediaLibrary = async (media, type) => {
      const result = await addMediaLibraryPost(hotelId, [
        { image_url: media, image_type: type },
      ]);
    };

    const updateImageCategory = (category, url) => {
            handleAddPostToMediaLibrary(url, category);
        switch (category) {
            case 'marketing':
                setLandscape((prev) => [...prev, url]);
                break;
            default:
                break;
        }
    };

    const handleDelete = (index, category) => {
        const categoryArray = getCategoryArray(category);
        if (!categoryArray) return;

        const updatedArray = categoryArray.filter((_, i) => i !== index);
        setCategoryArray(category, updatedArray);
    };

    const getCategoryArray = (category) => {
        switch (category) {
            case 'marketing':
                return landscape;
            default:
                return null;
        }
    };

    const setCategoryArray = (category, updatedArray) => {
        switch (category) {
            case 'marketing':
                setLandscape(updatedArray);
                break;
            default:
                break;
        }
    };

    const formatCategoryKey = (key) => {
        return key
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^[a-z]/, (char) => char.toUpperCase());
    };

    const renderCategory = (categoryKey) => {
        const categoryArray = getCategoryArray(categoryKey);
        if (!categoryArray) return null;

        const selectedUrl = categoryKey === "marketing" ? initialSelectedImage : null;
        const sortedArray = selectedUrl
            ? [selectedUrl, ...categoryArray.filter((url) => url !== selectedUrl)]
            : categoryArray;

        return sortedArray.map((url, index) => {
            const categoryError = errors[`${categoryKey}Image`];

            const errorMessage =
                validationErrors[url] ||
                (typeof categoryError === "string"
                    ? categoryError
                    : categoryError?.[`index-${index}`]) ||
                null;

            const isHovered = hoveredIndex === index;
            const isSelected = categoryKey === "marketing" && selectedMarketingImage === url;
            return (
              <div
                className={`image-card ${errorMessage != null ? "error" : ""}`}
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <img src={url} alt="Uploaded" />
                {(isHovered || errorMessage != null || isSelected) && (
                  <div
                    className={`image-overlay ${
                      errorMessage != null ? "has-error" : ""
                    } ${isSelected ? "selected" : ""}`}
                  >
                    {errorMessage && (
                      <p className="error-message">{errorMessage}</p>
                    )}
                    <CustomCheckbox
                      checkboxStyles={{
                        position: "absolute",
                        left: "2px",
                        bottom: "5px",
                      }}
                      checked={
                        (categoryKey === "marketing" &&
                          selectedMarketingImage === url)
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (categoryKey === "marketing") {
                          setSelectedMarketingImage(
                            isChecked ? url : null
                          );
                          onChangeSelection(categoryKey, isChecked ? url : null);
                        }
                      }}
                    />
                    <div className="image-actions">
                      <IconButton
                        color="primary"
                        onClick={() => setFullScreenImage(url)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="secondary"
                        className="delete-icon"
                        onClick={() => handleDelete(index, categoryKey)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </div>
                )}
              </div>
            );
        });
    };

    return (
        <div className="cloudinary-image-editor">
            <Helmet>
                <script src={APP_CLOUDINARY_URL_ENDPOINT} type="text/javascript" />
            </Helmet>

            <div className="image-grid">
                {CLOUDINARY_IMAGE_CATEGORIES.map((category) => (
                    <div className="cloudinary-category" key={category}>
                        <div className="category-header">
                            <h4>
                                <div className='category-header-title'>
                                {formatCategoryKey(category)}
                                {typeof errors[`${category}Image`] === "string" && (
                                    <Typography className="text-error-section">
                                        {errors[`${category}Image`]}
                                    </Typography>
                                )}
                                </div>
                                <AddCircleOutlineIcon
                                    className="add-icon"
                                    onClick={() => openUploadWidget(category)}
                                />
                            </h4>
                        </div>
                        <div className="image-category">{renderCategory(category)}</div>
                    </div>
                ))}
            </div>

            {fullScreenImage && (
                <div className="full-screen-overlay" onClick={() => setFullScreenImage(null)}>
                    <img src={removeCropParameters(fullScreenImage)} alt="Full Screen" className="full-screen-image" />
                </div>
            )}
        </div>
    );
};

export default CloudinaryImageEditor;